import { RefObject, useEffect, useState } from "react";
import { hrefFor, SearchItem } from "./types";

type Args = {
  open: boolean;
  items: SearchItem[];
  /** Changes when the result set changes → selection goes back to the top. */
  resetKey: string;
  listRef: RefObject<HTMLDivElement>;
  onSelect: (item: SearchItem) => void;
  onClose: () => void;
};

/**
 * Keyboard for the palette: Esc closes, arrows move, Enter selects and
 * ⌘/Ctrl+Enter opens the active row in a new tab (mirrors ⌘/Ctrl+click).
 * Keeps the active row scrolled into view.
 */
export function useSearchKeyboard({ open, items, resetKey, listRef, onSelect, onClose }: Args) {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    setActiveIdx(0);
  }, [resetKey, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (items.length ? (i + 1) % items.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
      } else if (e.key === "Enter") {
        const item = items[activeIdx];
        if (!item) return;
        e.preventDefault();
        if (e.metaKey || e.ctrlKey) window.open(hrefFor(item), "_blank", "noopener,noreferrer");
        else onSelect(item);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, items, activeIdx, onClose, onSelect]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${activeIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, listRef]);

  return { activeIdx, setActiveIdx };
}
